import { getLinkComment, getPortalLabel, normalizeLinkStatus, splitAdvertiseLinks } from '../../../functions/agent/advertises/portalLinks'
import { generateBgAndBorderCol } from '../../../functions/agent/schedule/generateBgAndBorderCol'
import {
    ADVERTISE_BUCKET_DEFINITIONS,
    buildAdvertiseSummary,
    buildOfferSummary,
    buildViewingSummary,
    getRelatedItems,
    getRelatedUnitId,
    isManualBsoAction,
    normalizeStatuses,
    OFFER_BUCKET_DEFINITIONS,
    splitRealTestCounts,
    type ActivityBucket,
    type ActivityBucketDefinition,
    type ActivityBucketKey,
    type EntityBreakdownSummary,
    VIEWING_BUCKET_DEFINITIONS
} from './adminBsoUnitStatistics'

const DUBAI_TIME_ZONE = 'Asia/Dubai'

export type UnitActivityTab = 'advertisements' | 'viewings' | 'offers'
export type ActivityFilterKey = 'all' | 'in_process' | 'completed' | 'rejected' | 'other'
export type ActivityKind = 'advertisements' | 'viewings' | 'offers'

export interface ActivityFilterOption {
    key: ActivityFilterKey
    label: string
    count: number
}

export interface AgentDisplay {
    id: number | null
    name: string
    company: string | null
    route: string | null
}

export interface ActivityLinkDisplay {
    label: string
    url: string | null
    trakheesiId: string | null
    status: string
    note: string | null
}

export interface ActivityDocumentDisplay {
    id: number | null
    label: string
    url: string | null
}

export interface ActivityFeedbackSummary {
    primary: string | null
    details: string[]
}

const getAttributes = (item: any): any => item?.attributes ?? item ?? {}

const toFiniteNumber = (value: unknown): number | null => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}

const toNonEmptyString = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : null

const toTitleCase = (value: string): string =>
    value
        .replace(/^documents\./, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())

const getRelationDataArray = (value: unknown): any[] => {
    const relation = (value as any)?.data ?? value
    return Array.isArray(relation) ? relation : []
}

export const getUnitAdvertisements = (unitId: number, advertises: any[]): any[] =>
    advertises.filter((advertise) => getRelatedUnitId(advertise) === unitId)

export const getUnitViewings = (unitId: number, viewings: any[]): any[] =>
    viewings.filter((viewing) => getRelatedUnitId(viewing) === unitId)

export const getUnitOffers = (unitId: number, offers: any[]): any[] =>
    offers.filter((offer) => getRelatedUnitId(offer) === unitId)

export const getUnitAdvertisementsFromStore = (unit: any, advertises: any[]): any[] =>
    getRelatedItems(unit, advertises, 'Advertises')

export const getUnitViewingsFromStore = (unit: any, viewings: any[]): any[] =>
    getRelatedItems(unit, viewings, 'Viewings')

export const getUnitOffersFromStore = (unit: any, offers: any[]): any[] =>
    getRelatedItems(unit, offers, 'Offers')

export const formatActivityDate = (value: string | null | undefined, withTime = false): string => {
    if (value == null || value.trim() === '') return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: DUBAI_TIME_ZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {})
    }).format(date)
}

export const formatActivityMoney = (value: unknown): string => {
    const amount = toFiniteNumber(value)
    return amount == null ? '-' : `AED ${amount.toLocaleString('en-US')}`
}

export const getStatusBadgeClassName = (status: string, item?: any): string => {
    const source = item ?? { attributes: { Statuses: [status] } }
    const color = generateBgAndBorderCol(source as any)
    return `badge-light-${color.borderColor} border border-${color.borderColor} text-${color.textColor}`
}

export const getPrimaryDateValue = (item: any, candidates: string[]): string | null => {
    const attributes = getAttributes(item)
    for (const key of candidates) {
        const value = toNonEmptyString(attributes?.[key])
        if (value != null) return value
    }
    return null
}

export const sortNewestFirst = (items: any[], candidates: string[]): any[] =>
    [...items].sort((left, right) => {
        const leftDate = getPrimaryDateValue(left, candidates)
        const rightDate = getPrimaryDateValue(right, candidates)
        const leftTime = leftDate != null ? new Date(leftDate).getTime() : 0
        const rightTime = rightDate != null ? new Date(rightDate).getTime() : 0
        if (leftTime !== rightTime) return rightTime - leftTime
        return Number(right?.id ?? 0) - Number(left?.id ?? 0)
    })

const findAgentById = (agentId: number | null, users: any[]): any | null =>
    agentId == null ? null : users.find((user) => Number(user?.id) === agentId) ?? null

const buildAgentName = (agent: any): string | null => {
    const contactName = [
        agent?.ContactInfo?.FirstName,
        agent?.ContactInfo?.FamilyName
    ]
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .join(' ')
        .trim()
    if (contactName !== '') return contactName

    const typeName = toNonEmptyString(agent?.Type?.[0]?.Name)
    if (typeName != null) return typeName

    return toNonEmptyString(agent?.username) ?? toNonEmptyString(agent?.email)
}

export const getAgentDisplay = (item: any, users: any[]): AgentDisplay => {
    const attributes = getAttributes(item)
    const relationUser = attributes?.User?.data
    const agentId = toFiniteNumber(relationUser?.id)
    const relationAttributes = relationUser?.attributes
    const storeAgent = findAgentById(agentId, users)
    const agent = storeAgent ?? relationAttributes ?? null
    const name = buildAgentName(agent) ?? (agentId != null ? `Agent #${String(agentId)}` : 'Unassigned')

    return {
        id: agentId,
        name,
        company: toNonEmptyString(agent?.Settings?.[0]?.Company),
        route: agentId != null ? `/landlord/profile/${String(agentId)}` : null
    }
}

const getMatchedBucket = (item: any, definitions: ActivityBucketDefinition[], now: Date): ActivityBucketKey => {
    const statuses = normalizeStatuses(item)
    const matched = definitions.find((definition) =>
        definition.key !== 'other' && statuses.some((status) => definition.matches(status, item, now))
    )
    return matched?.key ?? 'other'
}

export const groupAdvertisementStatus = (advertise: any, now: Date = new Date()): ActivityBucketKey =>
    getMatchedBucket(advertise, ADVERTISE_BUCKET_DEFINITIONS, now)

export const groupViewingStatus = (viewing: any, now: Date = new Date()): ActivityBucketKey =>
    getMatchedBucket(viewing, VIEWING_BUCKET_DEFINITIONS, now)

export const groupOfferStatus = (offer: any, now: Date = new Date()): ActivityBucketKey =>
    getMatchedBucket(offer, OFFER_BUCKET_DEFINITIONS, now)

export const getActivityBucketKey = (kind: ActivityKind, item: any, now: Date = new Date()): ActivityBucketKey => {
    if (kind === 'advertisements') return groupAdvertisementStatus(item, now)
    if (kind === 'viewings') return groupViewingStatus(item, now)
    return groupOfferStatus(item, now)
}

export const buildActivitySummary = (kind: ActivityKind, items: any[], now: Date = new Date(), fallbackIsDev = false): EntityBreakdownSummary => {
    const summary = kind === 'advertisements'
        ? buildAdvertiseSummary(items, now)
        : kind === 'viewings'
            ? buildViewingSummary(items, now)
            : buildOfferSummary(items, now)
    return {
        ...summary,
        countBreakdown: splitRealTestCounts(items, fallbackIsDev),
        manualByBsoCount: items.filter(isManualBsoAction).length
    }
}

const countBuckets = (summary: EntityBreakdownSummary, keys: ActivityBucketKey[]): number =>
    summary.buckets
        .filter((bucket) => keys.includes(bucket.key))
        .reduce((total, bucket) => total + bucket.count, 0)

export const getActivityFilterOptions = (summary: EntityBreakdownSummary): ActivityFilterOption[] => {
    const options: ActivityFilterOption[] = [
        { key: 'all', label: 'All', count: summary.total },
        { key: 'in_process', label: 'Active / In process', count: countBuckets(summary, ['in_process', 'negotiation']) },
        { key: 'completed', label: 'Completed / Closed', count: countBuckets(summary, ['completed']) },
        { key: 'rejected', label: 'Rejected / Cancelled', count: countBuckets(summary, ['rejected']) }
    ]
    const otherCount = countBuckets(summary, ['other'])
    if (otherCount > 0) options.push({ key: 'other', label: 'Other', count: otherCount })
    return options
}

export const filterActivityItems = (kind: ActivityKind, items: any[], filter: ActivityFilterKey, now: Date = new Date()): any[] => {
    if (filter === 'all') return items
    return items.filter((item) => {
        const bucketKey = getActivityBucketKey(kind, item, now)
        if (filter === 'in_process') return bucketKey === 'in_process' || bucketKey === 'negotiation'
        return bucketKey === filter
    })
}

export const getBucketLabel = (summary: EntityBreakdownSummary, key: ActivityBucketKey): string =>
    summary.buckets.find((bucket: ActivityBucket) => bucket.key === key)?.label ?? 'Other'

export const getAdvertisementLinks = (advertise: any): ActivityLinkDisplay[] => {
    const links = splitAdvertiseLinks(getAttributes(advertise)?.Links).all
    return links.map((link) => ({
        label: getPortalLabel(link),
        url: toNonEmptyString(link.Link),
        trakheesiId: toNonEmptyString(link.Trakheesi_id),
        status: normalizeLinkStatus(link.Status),
        note: toNonEmptyString(getLinkComment(link))
    }))
}

export const getViewingVisitorDisplay = (viewing: any): string | null => {
    const attributes = getAttributes(viewing)
    return toNonEmptyString(attributes?.VisitorName) ??
        toNonEmptyString(attributes?.ClientName) ??
        toNonEmptyString(attributes?.CustomerName) ??
        toNonEmptyString(attributes?.TenantName)
}

export const getViewingNotifyDisplay = (notify: unknown): string => {
    const value = toNonEmptyString(notify)
    if (value == null) return 'Read / Viewed'
    if (value === 'Landlord') return 'Unread by landlord'
    if (value === 'Agent' || value === 'Other Agent') return 'Unread by agent'
    if (value === 'Both') return 'Unread by landlord and agent'
    return value
}

const flattenFeedbackValue = (value: unknown): string[] => {
    if (value == null) return []
    if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed === '' ? [] : [trimmed]
    }
    if (typeof value === 'number' || typeof value === 'boolean') return [String(value)]
    if (Array.isArray(value)) return value.flatMap(flattenFeedbackValue)
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>)
            .flatMap(([key, entry]) => flattenFeedbackValue(entry).map((item) => `${toTitleCase(key)}: ${item}`))
    }
    return []
}

export const getViewingFeedbackSummary = (feedback: unknown): ActivityFeedbackSummary => {
    const raw = toNonEmptyString(feedback)
    if (raw == null) return { primary: null, details: [] }

    if ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'))) {
        try {
            const parsed = JSON.parse(raw)
            const flattened = flattenFeedbackValue(parsed)
            return {
                primary: flattened[0] ?? null,
                details: flattened.slice(1, 4)
            }
        } catch {
            return { primary: raw, details: [] }
        }
    }

    const parts = raw
        .split(/\s*\|\s*|\r?\n/)
        .map((item) => item.trim())
        .filter((item) => item !== '')

    return {
        primary: parts[0] ?? raw,
        details: parts.slice(1, 4)
    }
}

export const getViewingRescheduleReason = (viewing: any): string | null => {
    const attributes = getAttributes(viewing)
    return toNonEmptyString(attributes?.RescheduleReason) ??
        toNonEmptyString(attributes?.StatusUpdateDescription) ??
        toNonEmptyString(attributes?.Reason)
}

export const getOfferType = (offer: any): any | null => {
    const type = getAttributes(offer)?.Type
    return Array.isArray(type) && type.length > 0 ? type[0] : null
}

export const buildOfferRoute = (offer: any): string | null => {
    const attributes = getAttributes(offer)
    const unitId = toFiniteNumber(attributes?.Unit?.data?.id ?? attributes?.Unit?.id)
    const offerId = toFiniteNumber(offer?.id)
    const offerType = toNonEmptyString(getOfferType(offer)?.__component)
    if (unitId == null || offerId == null || offerType == null) return null
    return `/landlord/modalView/magic_offer_modal/${String(unitId)}/${String(offerId)}/catalog?type=${encodeURIComponent(offerType)}`
}

export const buildViewingRoute = (viewing: any): string | null => {
    const attributes = getAttributes(viewing)
    const unitId = toFiniteNumber(attributes?.Unit?.data?.id ?? attributes?.Unit?.id)
    const viewingId = toFiniteNumber(viewing?.id)
    if (unitId == null || viewingId == null) return null
    return `/landlord/modalView/magic_viewing_modal/${String(unitId)}/${String(viewingId)}/calendar`
}

export const buildAdvertiseRoute = (advertise: any): string | null => {
    const attributes = getAttributes(advertise)
    const unitId = toFiniteNumber(attributes?.Unit?.data?.id ?? attributes?.Unit?.id)
    const advertiseId = toFiniteNumber(advertise?.id)
    if (unitId == null || advertiseId == null) return null
    return `/landlord/modalView/magic_advertise_modal/${String(unitId)}/${String(advertiseId)}/tasks`
}

const getDocumentLabel = (document: any): string => {
    const attributes = getAttributes(document)
    const docType = attributes?.Type?.[0] ?? attributes?.Type
    return toNonEmptyString(attributes?.Category) ??
        toNonEmptyString(attributes?.Name) ??
        toNonEmptyString(attributes?.Title) ??
        toNonEmptyString(docType?.Name) ??
        toNonEmptyString(docType?.Title) ??
        toNonEmptyString(docType?.__component != null ? toTitleCase(String(docType.__component)) : null) ??
        toNonEmptyString(attributes?.RefID) ??
        (document?.id != null ? `Document #${String(document.id)}` : 'Document')
}

const buildDocumentDisplays = (document: any): ActivityDocumentDisplay[] => {
    const attributes = getAttributes(document)
    const label = getDocumentLabel(document)
    const links = [
        { label, url: toNonEmptyString(attributes?.Link) ?? toNonEmptyString(attributes?.URL) ?? toNonEmptyString(attributes?.Url) },
        { label: `${label} front`, url: toNonEmptyString(attributes?.LinkFront) },
        { label: `${label} back`, url: toNonEmptyString(attributes?.LinkBack) }
    ].filter((entry): entry is { label: string, url: string } => entry.url != null)

    return links.map((entry) => ({
        id: toFiniteNumber(document?.id),
        label: entry.label,
        url: entry.url
    }))
}

export const getOfferDocuments = (offer: any, documentsStore: any[] = []): ActivityDocumentDisplay[] => {
    const attributes = getAttributes(offer)
    const relationDocuments = getRelationDataArray(attributes?.Documents)
    const relationIds = new Set(relationDocuments.map((document) => toFiniteNumber(document?.id)).filter((id): id is number => id != null))
    const storeDocuments = documentsStore.filter((document) => {
        const documentOfferId = toFiniteNumber(getAttributes(document)?.Offer?.data?.id ?? getAttributes(document)?.Offer?.id)
        return documentOfferId === Number(offer?.id) || relationIds.has(Number(document?.id))
    })
    const documents = [...relationDocuments, ...storeDocuments].filter((document, index, arr) => {
        const id = toFiniteNumber(document?.id)
        if (id == null) return true
        return arr.findIndex((candidate) => toFiniteNumber(candidate?.id) === id) === index
    })

    return documents.flatMap(buildDocumentDisplays)
}

export const getOfferDocumentFields = (offer: any): ActivityDocumentDisplay[] => {
    const offerType = getOfferType(offer)
    const fields = [
        { label: 'Deposit slip', url: toNonEmptyString(offerType?.DepositSlipLink) },
        { label: 'Contract', url: toNonEmptyString(offerType?.ContractLink) },
        { label: 'Booking form', url: toNonEmptyString(offerType?.BookingFormLink) },
        { label: 'F Form', url: toNonEmptyString(offerType?.FFormLink) },
        { label: 'NOC', url: toNonEmptyString(offerType?.NOCLink) ?? toNonEmptyString(offerType?.NocLink) }
    ]

    return fields
        .filter((field): field is { label: string, url: string } => field.url != null)
        .map((field) => ({ id: null, label: field.label, url: field.url }))
}

export const getOfferNegotiationNotes = (offer: any): string[] => {
    const attributes = getAttributes(offer)
    const offerType = getOfferType(offer)
    return [
        toNonEmptyString(attributes?.RejectReason),
        toNonEmptyString(attributes?.StatusUpdateDescription),
        toNonEmptyString(offerType?.AdditionalNotes),
        toNonEmptyString(offerType?.NegotiationReason),
        toNonEmptyString(offerType?.NegotiationCategory)
    ].filter((value): value is string => value != null)
}

export const getOfferClientDetails = (offer: any): string[] => {
    const offerType = getOfferType(offer)
    return [
        toNonEmptyString(offerType?.Email),
        toNonEmptyString(offerType?.Phone),
        toNonEmptyString(offerType?.Occupation) != null ? `Occupation: ${String(offerType.Occupation)}` : null,
        toNonEmptyString(offerType?.Employer) != null ? `Employer: ${String(offerType.Employer)}` : null,
        toNonEmptyString(offerType?.BusinessName) != null ? `Business: ${String(offerType.BusinessName)}` : null,
        toNonEmptyString(offerType?.BusinessOwnerName) != null ? `Owner: ${String(offerType.BusinessOwnerName)}` : null
    ].filter((value): value is string => value != null)
}

export const getOfferContractDetails = (offer: any): Array<{ label: string, value: string }> => {
    const attributes = getAttributes(offer)
    const offerType = getOfferType(offer)
    return [
        { label: 'Contract start', value: formatActivityDate(offerType?.ContractStartDate) },
        { label: 'Duration', value: offerType?.Duration != null ? `${String(offerType.Duration)} months` : '-' },
        { label: 'Payments', value: offerType?.Payments != null ? String(offerType.Payments) : '-' },
        { label: 'Process start', value: formatActivityDate(attributes?.ProcessStartDate) },
        { label: 'Process end', value: formatActivityDate(attributes?.ProcessEndDate) },
        { label: 'Contract B', value: toNonEmptyString(offerType?.ContractBNumber) ?? '-' }
    ].filter((item) => item.value !== '-')
}

export const getOfferAmountDetails = (offer: any): Array<{ label: string, value: string }> => {
    const offerType = getOfferType(offer)
    return [
        { label: 'Amount', value: formatActivityMoney(offerType?.Offer) },
        { label: 'Payments', value: offerType?.Payments != null ? String(offerType.Payments) : '-' },
        { label: 'Duration', value: offerType?.Duration != null ? `${String(offerType.Duration)} months` : '-' },
        { label: 'Start', value: formatActivityDate(offerType?.ContractStartDate) }
    ].filter((item) => item.value !== '-')
}

export const getOverviewValue = (kind: ActivityKind, item: any): string | null => {
    const attributes = getAttributes(item)
    if (kind === 'advertisements') {
        const credits = attributes?.CreditsAmount != null ? `Credits ${String(attributes.CreditsAmount)}` : null
        const duration = attributes?.Duration != null ? `${String(attributes.Duration)} days` : null
        return credits ?? duration
    }
    if (kind === 'viewings') {
        return getViewingVisitorDisplay(item) ?? getViewingNotifyDisplay(attributes?.Notify)
    }
    const offerType = getOfferType(item)
    return formatActivityMoney(offerType?.Offer)
}
